import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, Loader2, ShoppingCart, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlendBuilderPage() {
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [ailments, setAilments] = useState([]);
  const [selectedAilments, setSelectedAilments] = useState([]);
  const [preferences, setPreferences] = useState('');
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [blendName, setBlendName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAilments();
  }, []);

  const fetchAilments = async () => {
    try {
      const response = await axios.get(`${API}/ailments`);
      setAilments(response.data.ailments);
    } catch (error) {
      console.error('Error fetching ailments:', error);
    }
  };

  const handleAilmentToggle = (ailment) => {
    setSelectedAilments(prev =>
      prev.includes(ailment)
        ? prev.filter(a => a !== ailment)
        : [...prev, ailment]
    );
  };

  const generateRecommendation = async () => {
    if (!user) {
      toast.error('Please login to use the blend builder');
      navigate('/auth');
      return;
    }

    if (selectedAilments.length === 0) {
      toast.error('Please select at least one ailment');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/blend/recommend`,
        {
          ailments: selectedAilments,
          preferences: preferences
        },
        { withCredentials: true }
      );
      setRecommendation(response.data);
      setBlendName(response.data.recommendation.blend_name || 'My Custom Blend');
      toast.success('Blend recommendation generated!');
    } catch (error) {
      toast.error('Failed to generate recommendation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveBlend = async () => {
    if (!blendName.trim()) {
      toast.error('Please enter a name for your blend');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/blend/create`,
        {
          name: blendName,
          ailments: selectedAilments,
          selected_tea_ids: recommendation.recommendation.tea_ids,
          recommendation_text: recommendation.recommendation.explanation
        },
        { withCredentials: true }
      );
      toast.success('Blend saved successfully!');
    } catch (error) {
      toast.error('Failed to save blend');
    } finally {
      setSaving(false);
    }
  };

  const addBlendToCart = async () => {
    try {
      // Add each tea in the blend to cart
      for (const tea of recommendation.suggested_teas) {
        await axios.post(
          `${API}/cart`,
          { item_id: tea.id, item_type: 'tea', quantity: 1 },
          { withCredentials: true }
        );
      }
      await fetchCartCount();
      toast.success('Blend added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.90), rgba(204, 85, 0, 0.90)), url(https://images.unsplash.com/photo-1666113802599-e4c8b2b9c3a1?q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }} data-testid="blend-builder-title">
            AI-Powered Custom Blend Builder
          </h1>
          <p className="text-xl text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>Let our AI herbalist create the perfect tea blend for your needs</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Selection Panel */}
          <Card className="bg-[#00A86B] backdrop-blur-sm shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Sparkles className="inline w-6 h-6 mr-2 text-white" />
                Step 1: What are you seeking help with?
              </CardTitle>
              <CardDescription className="text-white">Select all ailments you'd like to address</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto" data-testid="ailments-list">
                {ailments.map(ailment => (
                  <div key={ailment} className="flex items-center space-x-2">
                    <Checkbox
                      id={ailment}
                      checked={selectedAilments.includes(ailment)}
                      onCheckedChange={() => handleAilmentToggle(ailment)}
                      data-testid={`ailment-${ailment}`}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-[#00A86B]"
                    />
                    <Label htmlFor={ailment} className="cursor-pointer text-sm text-white">
                      {ailment}
                    </Label>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-white/30">
                <Label htmlFor="preferences" className="text-base font-semibold mb-2 block text-white">
                  Additional Preferences (Optional)
                </Label>
                <Textarea
                  id="preferences"
                  placeholder="E.g., I prefer caffeine-free teas, I don't like strong flavors, etc."
                  value={preferences}
                  onChange={(e) => setPreferences(e.target.value)}
                  className="min-h-24 bg-white"
                  data-testid="preferences-input"
                />
              </div>

              <Button
                onClick={generateRecommendation}
                disabled={loading || selectedAilments.length === 0}
                className="w-full bg-white text-[#00A86B] hover:bg-white/90 text-lg py-6"
                data-testid="generate-recommendation-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Consulting AI Herbalist...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate My Blend
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Recommendation Panel */}
          <Card className="bg-[#00A86B] backdrop-blur-sm shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Custom Blend
              </CardTitle>
              <CardDescription className="text-white">AI-generated recommendation based on your needs</CardDescription>
            </CardHeader>
            <CardContent>
              {!recommendation ? (
                <div className="text-center py-12 text-white">
                  <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select your ailments and generate a recommendation to see your custom blend here</p>
                </div>
              ) : (
                <div className="space-y-6" data-testid="recommendation-result">
                  <div>
                    <Label className="text-base font-semibold mb-2 block text-white">Blend Name:</Label>
                    <Input
                      value={blendName}
                      onChange={(e) => setBlendName(e.target.value)}
                      className="text-lg bg-white"
                      data-testid="blend-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-2 block text-white">Why This Works:</Label>
                    <p className="text-sage-900 leading-relaxed bg-white p-4 rounded-lg">
                      {recommendation.recommendation.explanation}
                    </p>
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-2 block text-white">Teas in Your Blend:</Label>
                    <div className="space-y-3">
                      {recommendation.suggested_teas.map(tea => (
                        <div
                          key={tea.id}
                          className="flex items-center gap-4 p-3 bg-cream-50 rounded-lg hover:bg-cream-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/tea/${tea.id}`)}
                          data-testid={`blend-tea-${tea.id}`}
                        >
                          <img src={tea.image_url} alt={tea.name} className="w-16 h-16 rounded object-cover" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-sage-900">{tea.name}</h4>
                            <p className="text-sm text-sage-600">{tea.category}</p>
                          </div>
                          <span className="text-lg font-bold text-terracotta-600">${tea.price.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-2 block text-white">Brewing Instructions:</Label>
                    <p className="text-sage-900 bg-white p-4 rounded-lg">
                      {recommendation.recommendation.brewing_instructions}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={saveBlend}
                      disabled={saving}
                      variant="outline"
                      className="flex-1 bg-white text-[#00A86B] hover:bg-white/90"
                      data-testid="save-blend-button"
                    >
                      {saving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Save Blend
                    </Button>
                    <Button
                      onClick={addBlendToCart}
                      className="flex-1 bg-white text-[#00A86B] hover:bg-white/90"
                      data-testid="add-blend-to-cart-button"
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Add to Cart
                    </Button>
                  </div>

                  <div className="text-center pt-4 border-t border-white/30">
                    <p className="text-sm text-white">
                      Total: <span className="text-xl font-bold text-white" data-testid="blend-total">
                        ${recommendation.suggested_teas.reduce((sum, tea) => sum + tea.price, 0).toFixed(2)}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <Card className="mt-12 bg-[#00A86B] backdrop-blur-sm shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-24 h-24 bg-white text-[#00A86B] rounded-full flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-[#00A86B] mb-2 text-lg">Select Your Ailments</h3>
                <p className="text-base text-[#00A86B]">Choose from our comprehensive list of health concerns</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-white text-[#00A86B] rounded-full flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                  2
                </div>
                <h3 className="font-semibold text-[#00A86B] mb-2 text-lg">AI Analysis</h3>
                <p className="text-base text-[#00A86B]">Our AI herbalist analyzes 150+ teas to create your perfect blend</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-white text-[#00A86B] rounded-full flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                  3
                </div>
                <h3 className="font-semibold text-[#00A86B] mb-2 text-lg">Save & Order</h3>
                <p className="text-base text-[#00A86B]">Save your blend for later or add to cart immediately</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
